// src/contexts/PlanContext.jsx
// Exposes the firm's current subscription plan and feature-gate helpers.

import React, { createContext, useContext, useEffect, useState } from 'react';
import { Lock, ArrowUpCircle, MessageSquare } from 'lucide-react';
import { useFirm } from './FirmContext';
import { usePermissions } from './PermissionsContext';
import { planIncludes, minPlanFor, PLANS } from '../lib/plans';
import { supabase } from '../services/supabaseClient';
import UpgradeModal from '../components/UpgradeModal';

const PlanContext = createContext(null);

export function PlanProvider({ children }) {
  const { firm } = useFirm();
  const [userCount, setUserCount] = useState(1);

  // Fetch active user count for this firm (used in pricing display)
  useEffect(() => {
    if (!firm?.id) return;
    supabase
      .from('users')
      .select('id', { count: 'exact', head: true })
      .eq('firm_id', firm.id)
      .eq('status', 'Active')
      .then(({ count }) => {
        if (count !== null) setUserCount(count);
      });
  }, [firm?.id]);

  const planKey  = firm?.subscription_plan ?? 'trial';
  const planMeta = PLANS[planKey] ?? PLANS.trial;

  /** Returns true if the current plan includes the given feature key. */
  function hasFeature(featureKey) {
    return planIncludes(planKey, featureKey);
  }

  /** Returns the minimum plan object needed for the feature. */
  function requiredPlanFor(featureKey) {
    return minPlanFor(featureKey);
  }

  const value = {
    planKey,
    planName:   planMeta.name,
    planMeta,
    userCount,
    hasFeature,
    requiredPlanFor,
  };

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
}

export function usePlan() {
  const ctx = useContext(PlanContext);
  if (!ctx) throw new Error('usePlan must be used within a PlanProvider');
  return ctx;
}

// ── PlanGate ─────────────────────────────────────────────────────────────────
// Wrap any page or section that requires a paid feature.
// Shows an upgrade prompt when the feature is not available on the current plan.

export function PlanGate({ feature, children }) {
  const { hasFeature, requiredPlanFor, planName } = usePlan();
  const { can } = usePermissions();
  const [showUpgrade, setShowUpgrade] = useState(false);

  if (hasFeature(feature)) return children;

  const required   = requiredPlanFor(feature);
  // Only firm admins (users who can edit settings / manage billing) can upgrade
  const canUpgrade = can('settings.edit');

  return (
    <>
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] p-8 text-center">
        {/* Icon */}
        <div className="w-16 h-16 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center mb-5">
          <Lock size={28} className="text-indigo-500" />
        </div>

        {/* Heading */}
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {required.name} plan required
        </h2>

        {/* Description */}
        <p className="text-gray-500 dark:text-slate-400 max-w-sm mb-1">
          This feature is included in the{' '}
          <strong className="text-gray-700 dark:text-slate-200">{required.name}</strong>{' '}
          plan (${required.priceUsd}/user/month) and above.
        </p>
        <p className="text-gray-400 dark:text-slate-500 text-sm mb-8">
          Your firm is currently on <strong>{planName}</strong>.
        </p>

        {canUpgrade ? (
          /* Admin: show upgrade button */
          <button
            onClick={() => setShowUpgrade(true)}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition"
          >
            <ArrowUpCircle size={16} />
            Upgrade to {required.name}
          </button>
        ) : (
          /* Non-admin: tell them to ask their admin */
          <div className="inline-flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl px-5 py-4 max-w-sm text-left">
            <MessageSquare size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              Contact your <strong>firm administrator</strong> to upgrade the subscription
              and unlock this feature.
            </p>
          </div>
        )}
      </div>

      {showUpgrade && canUpgrade && (
        <UpgradeModal
          highlightPlan={required.key}
          onClose={() => setShowUpgrade(false)}
        />
      )}
    </>
  );
}
